import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useRoutes, useLocation, useNavigate } from 'react-router-dom';
import { auth } from './config/firebase';
import Toolbar from './components/Toolbar';
import BottomBar from './components/BottomBar';
import { Helmet } from 'react-helmet-async';
import './index.scss';
import { useNavigation } from './config/NavigationContext';
import NavigationFrame from './components/NavigationFrame/NavigationFrame';
import routes from './config/routes';
import ModalManager from './components/ModalManager';
import LazyLoader from './components/LazyLoader';
import ScrollContainer from './components/ScrollContainer/ScrollContainer';
import HandleClaimModal from './components/HandleClaimModal/HandleClaimModal';
import SharedSongLayout from './components/SharedSongLayout';
import { useUser } from './config/UserContext';
import { clearChunkReloadFlag } from './utils/lazyWithRetry';

import logger from './utils/logger';

function App({
  openSongPage: parentOpenSongPage,
  closeSongPage: parentCloseSongPage,
  currentSong,
  isMinimized,
}) {
  const [loading, setLoading] = useState(true);
  const { currentUser } = useUser();
  const needsHandle = !!currentUser && !currentUser.handleConfirmed;
  const [isMobile, setIsMobile] = useState(window.innerWidth < 821);
  const { handleUserPageNav, handleHomeNav, handleNotificationsNav } =
    useNavigation();

  const scrollPositionRef = useRef(0);
  const navWindowRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const forwardVerticalWheel = useCallback((containerRef, e) => {
    /* Skip when hovering over a page that has its own ScrollContainer
       (SongListHome, UserPage) so their inner scroll receives the wheel. */
    if (e.target.closest('[data-has-own-scroll]')) return;

    if (!containerRef.current) return;
    const isVertical = Math.abs(e.deltaY) > Math.abs(e.deltaX);
    if (!isVertical) return;

    containerRef.current.scrollTop += e.deltaY;
    e.stopPropagation();
  }, []);
  const siteUrl = 'https://goodsong.club';
  const params = new URLSearchParams(location.search);
  const songId = params.get('songId');
  const userId = params.get('userId');
  const isLegacySongQueryRoute = location.pathname === '/' && !!songId;
  const isLegacyUserQueryRoute = location.pathname === '/' && !!userId;
  const canonicalPath = isLegacySongQueryRoute
    ? `/song/${encodeURIComponent(songId)}`
    : isLegacyUserQueryRoute
      ? `/user/${encodeURIComponent(userId)}`
      : location.pathname;
  const canonicalUrl = `${siteUrl}${canonicalPath}`;
  const shouldNoindex =
    location.pathname === '/login' ||
    location.pathname === '/search' ||
    location.pathname === '/performance-demo' ||
    location.pathname.startsWith('/notifs/');

  // Check if current route should be rendered standalone (without app structure)
  const isStandaloneRoute =
    location.pathname === '/tos' || location.pathname === '/privacy';

  // Shared song landing (`/song/:id?shared=1`) also renders outside the app
  // shell, but with its own branded wrapper + "Open in app" CTA rather than
  // the plain TOS/privacy template.
  const isSharedSongRoute =
    location.pathname.startsWith('/song/') && params.get('shared') === '1';

  const handleResize = useCallback(() => {
    setIsMobile(window.innerWidth < 821);
  }, []);

  //TEST FOR ERROR BOUNDARY
  // if (process.env.NODE_ENV === 'development') {
  //   throw new Error('Manual ErrorBoundary test');
  // }

  useEffect(() => {
    auth.onAuthStateChanged(() => {
      setLoading(false);
    });

    // App rendered successfully on the current bundle — reset the
    // post-deploy reload guard so the next stale-bundle scenario can
    // trigger another one-shot reload.
    clearChunkReloadFlag();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  // URL → state: open the song/user overlay, or redirect a bare `/song/:id`
  // deep link onto the legacy `?songId=…` form the multi-panel shell expects.
  //
  // Intentionally does NOT close the overlay when songId disappears from the
  // URL. The multi-panel shell treats `currentSong` as a persistent playback
  // layer that survives route changes — e.g. clicking the author link in
  // SongPage should load the user page in navWindow while the song keeps
  // playing. Only the explicit X-button close clears the overlay.
  useEffect(() => {
    if (songId) {
      parentOpenSongPage(songId);
      return;
    }
    if (userId) {
      handleUserPageNav(userId);
      return;
    }

    if (!isSharedSongRoute) {
      const songMatch = location.pathname.match(/^\/song\/([^/]+)/);
      if (songMatch?.[1]) {
        const nextParams = new URLSearchParams(location.search);
        nextParams.set('songId', songMatch[1]);
        navigate(`/?${nextParams.toString()}`, { replace: true });
      }
    }
  }, [
    songId,
    userId,
    parentOpenSongPage,
    handleUserPageNav,
    isSharedSongRoute,
    location.pathname,
    location.search,
    navigate,
  ]);

  const openSongPage = useCallback(
    (songId, commentId, replyId) => {
      logger.debug('openSongPage called with songId:', songId);
      scrollPositionRef.current = window.scrollY;
      parentOpenSongPage(songId, commentId, replyId);
    },
    [parentOpenSongPage]
  );

  // Create routes at the top level (React Hook rules)
  const routeElements = useRoutes(
    routes.map((route) => ({
      path: route.path,
      element: (
        <LazyLoader>
          <route.component
            openSongPage={openSongPage}
            handleUserPageNav={handleUserPageNav}
            handleHomeNav={handleHomeNav}
            handleNotificationsNav={handleNotificationsNav}
          />
        </LazyLoader>
      ),
    }))
  );

  if (loading) {
    return <div className="loading-container"></div>;
  }

  // Render shared song links as a focused landing page with its own wrapper.
  // SongPage metadata (title, og:*) still comes from within the route element.
  if (isSharedSongRoute) {
    return (
      <>
        <Helmet>
          <meta charSet="utf-8" />
          <link rel="canonical" href={canonicalUrl} />
          <link rel="icon" href="https://goodsong.club/favicon.ico" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <meta name="theme-color" content="#fcd34d" />
        </Helmet>
        <SharedSongLayout>{routeElements}</SharedSongLayout>
        <ModalManager />
      </>
    );
  }

  // Render standalone pages without app structure
  if (isStandaloneRoute) {
    return (
      <>
        <Helmet>
          <meta charSet="utf-8" />
          <title>
            {location.pathname === '/tos'
              ? 'Terms of Service - Good Song Club'
              : 'Privacy Policy - Good Song Club'}
          </title>
          <link rel="canonical" href={canonicalUrl} />
          <link rel="icon" href="https://goodsong.club/favicon.ico" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </Helmet>
        <div
          style={{
            minHeight: '100vh',
            padding: '20px',
            maxWidth: '800px',
            margin: '0 auto',
          }}
        >
          {routeElements}
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Good Song Club</title>
        <link rel="canonical" href={canonicalUrl} />
        <link rel="icon" href="https://goodsong.club/favicon.ico" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#000000" />
        <meta name="title" content="Good Song Club" />
        <meta name="description" content="Share good songs from YouTube." />
        {shouldNoindex && (
          <meta name="robots" content="noindex, nofollow, noarchive" />
        )}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content="Good Song Club" />
        <meta
          property="og:description"
          content="Share good songs from YouTube."
        />
        <meta property="og:image" content="https://goodsong.club/ogimage.png" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={canonicalUrl} />
        <meta property="twitter:title" content="Good Song Club" />
        <meta
          property="twitter:description"
          content="Share good songs from YouTube."
        />
        <meta
          property="twitter:image"
          content="https://goodsong.club/ogimage.png"
        />
        <link rel="apple-touch-icon" href="https://goodsong.club/logo192.png" />
        <link rel="manifest" href="https://goodsong.club/manifest.json" />
      </Helmet>

      {isMobile ? (
        <>
          {(!currentSong || isMinimized) && <BottomBar openSongPage={openSongPage} />}
          <ScrollContainer
            ref={navWindowRef}
            className="navWindow"
            style={{ display: (currentSong && !isMinimized) ? 'none' : 'block' }}
            onWheelCapture={(e) => forwardVerticalWheel(navWindowRef, e)}
          >
            <NavigationFrame openSongPage={openSongPage} />
            {routeElements}
          </ScrollContainer>
        </>
      ) : (
        <>
          <Toolbar className="toolbar" handleUserPageNav={handleUserPageNav} />
          <ScrollContainer
            ref={navWindowRef}
            className="navWindow"
            onWheelCapture={(e) => forwardVerticalWheel(navWindowRef, e)}
          >
            <NavigationFrame openSongPage={openSongPage} />
            {routeElements}
          </ScrollContainer>
        </>
      )}

      <ModalManager />
      {needsHandle && <HandleClaimModal />}
    </>
  );
}

export default App;
